import { ChangeEvent } from "react";
import { Show } from "shared/components";
import { ApartmentsComVariant, ZillowVariant } from "types/landlord/address";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { BoomBrandMark } from "./BoomBrandMark";
import { SourceCheckbox } from "./SourceCheckbox";
import { SourceLogo } from "./SourceLogo";
import { TreeRow } from "./TreeRow";
import { SyndicationState } from "./syndication";

const noop = () => undefined;

const isolate =
  (handler: () => void) => (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    handler();
  };

type FeedVariant = ApartmentsComVariant | ZillowVariant;

const VariantRows = ({
  selected,
  checked,
  disabled,
  onPick
}: {
  selected: FeedVariant | null;
  checked: boolean;
  disabled: boolean;
  onPick: (variant: FeedVariant) => void;
}) => {
  const pick = (variant: FeedVariant) => () => {
    if (selected === variant) return;
    onPick(variant);
  };

  return (
    <div className="mt-3 space-y-3 pl-8">
      <TreeRow spacing="base">
        <SourceCheckbox
          label="Single Family"
          checked={checked && selected === "single_family"}
          disabled={disabled}
          onChange={isolate(pick("single_family"))}
        />
      </TreeRow>
      <TreeRow isLast spacing="base">
        <SourceCheckbox
          label="Multi Family"
          checked={checked && selected === "multi_family"}
          disabled={disabled}
          onChange={isolate(pick("multi_family"))}
        />
      </TreeRow>
    </div>
  );
};

export const SyndicationChannelTree = ({
  syndication,
  unitListed,
  hasListing,
  zillowEnabled,
  apartmentsComEnabled,
  zumperEnabled,
  dwellsyEnabled,
  realtorComEnabled,
  onToggleZillow,
  onPickZillowVariant,
  onToggleApartmentsCom,
  onPickApartmentsVariant,
  onToggleZumper,
  onToggleDwellsy,
  onToggleRealtorCom
}: {
  syndication: SyndicationState;
  unitListed: boolean;
  hasListing: boolean;
  zillowEnabled: boolean;
  apartmentsComEnabled: boolean;
  zumperEnabled: boolean;
  dwellsyEnabled: boolean;
  realtorComEnabled: boolean;
  onToggleZillow: () => void;
  onPickZillowVariant: (variant: ZillowVariant) => void;
  onToggleApartmentsCom: () => void;
  onPickApartmentsVariant: (variant: ApartmentsComVariant) => void;
  onToggleZumper: () => void;
  onToggleDwellsy: () => void;
  onToggleRealtorCom: () => void;
}) => {
  const sectionDisabled = !unitListed || !hasListing;

  return (
    <div className="space-y-3">
      <SourceCheckbox
        label="Portfolio page"
        icon={<BoomBrandMark />}
        checked={unitListed}
        disabled
        onChange={isolate(noop)}
      />
      <SourceCheckbox
        label="Listings page"
        icon={<BoomBrandMark />}
        checked={unitListed}
        disabled
        onChange={isolate(noop)}
      />
      <Show if={zumperEnabled}>
        <SourceCheckbox
          label="Zumper"
          icon={<SourceLogo id={IntegrationId.ZUMPER} />}
          checked={unitListed && syndication.zumper}
          disabled={sectionDisabled}
          onChange={isolate(onToggleZumper)}
        />
      </Show>
      <Show if={zillowEnabled}>
        <div>
          <SourceCheckbox
            label="Zillow"
            icon={<SourceLogo id={IntegrationId.ZILLOW} />}
            checked={unitListed && syndication.zillow}
            disabled={sectionDisabled}
            onChange={isolate(onToggleZillow)}
          />
          <VariantRows
            selected={syndication.zillowVariant}
            checked={unitListed && syndication.zillow}
            disabled={sectionDisabled || !syndication.zillow}
            onPick={onPickZillowVariant}
          />
        </div>
      </Show>
      <Show if={apartmentsComEnabled}>
        <div>
          <SourceCheckbox
            label="Apartments.com"
            icon={<SourceLogo id={IntegrationId.APARTMENTS_COM} />}
            checked={unitListed && syndication.apartmentsCom}
            disabled={sectionDisabled}
            onChange={isolate(onToggleApartmentsCom)}
          />
          <VariantRows
            selected={syndication.apartmentsComVariant}
            checked={unitListed && syndication.apartmentsCom}
            disabled={sectionDisabled || !syndication.apartmentsCom}
            onPick={onPickApartmentsVariant}
          />
        </div>
      </Show>
      <Show if={dwellsyEnabled}>
        <SourceCheckbox
          label="Dwellsy"
          icon={<SourceLogo id={IntegrationId.DWELLSY} />}
          checked={unitListed && syndication.dwellsy}
          disabled={sectionDisabled}
          onChange={isolate(onToggleDwellsy)}
        />
      </Show>
      <Show if={realtorComEnabled}>
        <SourceCheckbox
          label="Realtor.com"
          icon={<SourceLogo id={IntegrationId.REALTOR_COM} />}
          checked={unitListed && syndication.realtorCom}
          disabled={sectionDisabled}
          onChange={isolate(onToggleRealtorCom)}
        />
      </Show>
    </div>
  );
};
